import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

async function assertAdmin(userId: string) {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data, error } = await supabaseAdmin
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .eq("role", "admin")
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Forbidden: admin role required");
}

// ---------- Current user role ----------
export const getMyRoles = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data } = await supabaseAdmin
      .from("user_roles")
      .select("role")
      .eq("user_id", context.userId);
    return { roles: (data ?? []).map((r) => r.role as string) };
  });

// ---------- Users ----------
export const adminListUsers = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const [{ data: profiles }, { data: roles }, { data: subs }] = await Promise.all([
      supabaseAdmin.from("profiles").select("*").order("created_at", { ascending: false }),
      supabaseAdmin.from("user_roles").select("user_id, role"),
      supabaseAdmin
        .from("subscriptions")
        .select("user_id, status, current_period_end, price_id, product_id, created_at")
        .order("created_at", { ascending: false }),
    ]);
    const rolesByUser = new Map<string, string[]>();
    (roles ?? []).forEach((r) => {
      const arr = rolesByUser.get(r.user_id) ?? [];
      arr.push(r.role as string);
      rolesByUser.set(r.user_id, arr);
    });
    const subByUser = new Map<
      string,
      { status: string; current_period_end: string | null; price_id: string; product_id: string }
    >();
    (subs ?? []).forEach((s) => {
      if (!subByUser.has(s.user_id)) {
        subByUser.set(s.user_id, {
          status: s.status,
          current_period_end: s.current_period_end as string | null,
          price_id: s.price_id,
          product_id: s.product_id,
        });
      }
    });
    return {
      users: (profiles ?? []).map((p) => ({
        id: p.id,
        email: p.email,
        display_name: p.display_name,
        full_name: p.full_name,
        plan: p.plan as string,
        banned: p.banned,
        created_at: p.created_at,
        last_seen_at: (p as { last_seen_at?: string | null }).last_seen_at ?? null,
        onboarded_at: (p as { onboarded_at?: string | null }).onboarded_at ?? null,
        roles: rolesByUser.get(p.id) ?? [],
        subscription: subByUser.get(p.id) ?? null,
      })),
    };
  });

export const adminSetUserRole = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z
      .object({
        userId: z.string().uuid(),
        role: z.enum(["admin", "user"]),
        grant: z.boolean(),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { writeAuditLog } = await import("@/lib/admin-audit.server");
    if (data.grant) {
      const { error } = await supabaseAdmin
        .from("user_roles")
        .upsert({ user_id: data.userId, role: data.role }, { onConflict: "user_id,role" });
      if (error) throw new Error(error.message);
    } else {
      const { error } = await supabaseAdmin
        .from("user_roles")
        .delete()
        .eq("user_id", data.userId)
        .eq("role", data.role);
      if (error) throw new Error(error.message);
    }
    await writeAuditLog({
      action: data.grant ? "ADMIN_GRANT_ROLE" : "ADMIN_REVOKE_ROLE",
      actorId: context.userId,
      targetType: "user",
      targetId: data.userId,
      oldValue: { granted: !data.grant, role: data.role },
      newValue: { granted: data.grant, role: data.role },
    });
    return { ok: true };
  });

export const adminSetUserBanned = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => z.object({ userId: z.string().uuid(), banned: z.boolean() }).parse(i))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { writeAuditLog } = await import("@/lib/admin-audit.server");
    const { data: prev } = await supabaseAdmin
      .from("profiles")
      .select("banned")
      .eq("id", data.userId)
      .maybeSingle();
    const { error } = await supabaseAdmin
      .from("profiles")
      .update({ banned: data.banned })
      .eq("id", data.userId);
    if (error) throw new Error(error.message);
    await writeAuditLog({
      action: "ADMIN_SET_USER_BANNED",
      actorId: context.userId,
      targetType: "user",
      targetId: data.userId,
      oldValue: { banned: prev?.banned ?? null },
      newValue: { banned: data.banned },
    });
    return { ok: true };
  });

export const adminSetUserPlan = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z
      .object({
        userId: z.string().uuid(),
        plan: z.enum(["free", "pro", "elite"]),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { writeAuditLog } = await import("@/lib/admin-audit.server");
    const { data: prev } = await supabaseAdmin
      .from("profiles")
      .select("plan")
      .eq("id", data.userId)
      .maybeSingle();
    const { error } = await supabaseAdmin
      .from("profiles")
      .update({ plan: data.plan as never })
      .eq("id", data.userId);
    if (error) throw new Error(error.message);
    await writeAuditLog({
      action: "ADMIN_SET_USER_PLAN",
      actorId: context.userId,
      targetType: "user",
      targetId: data.userId,
      oldValue: { plan: prev?.plan ?? null },
      newValue: { plan: data.plan },
    });
    return { ok: true };
  });

export const adminExtendSubscription = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z.object({ userId: z.string().uuid(), days: z.number().int().min(1).max(365) }).parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { writeAuditLog } = await import("@/lib/admin-audit.server");
    const { data: sub } = await supabaseAdmin
      .from("subscriptions")
      .select("id, current_period_end")
      .eq("user_id", data.userId)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (!sub) throw new Error("No subscription found for this user");
    const base = sub.current_period_end ? new Date(sub.current_period_end) : new Date();
    const extended = new Date(base.getTime() + data.days * 24 * 60 * 60 * 1000);
    const { error } = await supabaseAdmin
      .from("subscriptions")
      .update({ current_period_end: extended.toISOString() })
      .eq("id", sub.id);
    if (error) throw new Error(error.message);
    await writeAuditLog({
      action: "ADMIN_EXTEND_SUBSCRIPTION",
      actorId: context.userId,
      targetType: "subscription",
      targetId: sub.id,
      oldValue: { current_period_end: sub.current_period_end ?? null },
      newValue: { current_period_end: extended.toISOString(), days_added: data.days },
      extra: { target_user_id: data.userId },
    });
    return { ok: true, new_end: extended.toISOString() };
  });

export const adminSendPasswordReset = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => z.object({ email: z.string().email() }).parse(i))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { writeAuditLog } = await import("@/lib/admin-audit.server");
    const { data: link, error } = await supabaseAdmin.auth.admin.generateLink({
      type: "recovery",
      email: data.email,
    });
    if (error) throw new Error(error.message);
    await writeAuditLog({
      action: "ADMIN_SEND_PASSWORD_RESET",
      actorId: context.userId,
      targetType: "user",
      targetId: null,
      newValue: { email: data.email },
    });
    return { ok: true, link: link?.properties?.action_link ?? null };
  });

export const adminDeleteUser = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => z.object({ userId: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    if (data.userId === context.userId) throw new Error("You cannot delete your own account");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { writeAuditLog } = await import("@/lib/admin-audit.server");
    const { data: prev } = await supabaseAdmin
      .from("profiles")
      .select("email, plan, banned")
      .eq("id", data.userId)
      .maybeSingle();
    const { error } = await supabaseAdmin.auth.admin.deleteUser(data.userId);
    if (error) throw new Error(error.message);
    await writeAuditLog({
      action: "ADMIN_DELETE_USER",
      actorId: context.userId,
      targetType: "user",
      targetId: data.userId,
      oldValue: prev ?? null,
      newValue: { deleted: true },
    });
    return { ok: true };
  });


// ---------- Signals ----------
export const adminListSignals = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("signals")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(200);
    if (error) throw new Error(error.message);
    return { signals: data ?? [] };
  });

export const adminDeleteSignal = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => z.object({ id: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin.from("signals").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const persistSignal = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z
      .object({
        pair: z.string().min(1).max(16),
        action: z.enum(["BUY", "SELL", "WAIT"]),
        score: z.number().min(0).max(100),
        entry: z.number().nullable().optional(),
        stopLoss: z.number().nullable().optional(),
        takeProfit: z.number().nullable().optional(),
        lots: z.number().nullable().optional(),
        agentVotes: z.string().max(500).nullable().optional(),
        reasoning: z.string().max(2000).nullable().optional(),
        playbookMatch: z
          .object({
            playbookName: z.string().min(1).max(120),
            legendArchitect: z.string().min(1).max(80),
            similarityPct: z.number().min(0).max(100),
            bias: z.enum(["BUY", "SELL", "WAIT"]),
          })
          .nullable()
          .optional(),
        // Ledger-only fields. Best-effort — missing values are stored as null.
        readiness: z.number().min(0).max(100).nullable().optional(),
        agentVotesJson: z.record(z.string(), z.unknown()).nullable().optional(),
        dataSources: z.record(z.string(), z.unknown()).nullable().optional(),
        degradedFlags: z.record(z.string(), z.unknown()).nullable().optional(),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    const { resolveSignalQuotaConfig } = await import("@/lib/rate-limit.functions");
    const { checkRateLimit } = await import("@/lib/security/rate-limit.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const cfg = await resolveSignalQuotaConfig(context.userId);

    // Per-minute burst protection — applies to every plan, including admin/elite.
    // Free: 1/min, Pro: 5/min, Elite/admin: 10/min.
    const perMinute = cfg.plan === "free" ? 1 : cfg.plan === "pro" ? 5 : 10;
    const burst = await checkRateLimit(`signal:${context.userId}`, perMinute, 60);
    if (!burst.allowed) {
      throw new Error(
        `Too many signal requests. Please wait a moment before generating another (limit: ${perMinute}/min).`,
      );
    }
    // Atomic claim + insert under a per-user advisory lock — prevents
    // burst-click bypass of the daily quota (race-condition fix).
    const { data: rpc, error: rpcError } = await supabaseAdmin.rpc(
      "insert_signal_with_quota",
      {
        p_user_id: context.userId,
        p_daily_limit: cfg.limit,
        p_unlimited: cfg.unlimited,
        p_pair: data.pair,
        p_action: data.action,
        p_score: data.score,
        p_entry: (data.entry ?? null) as number,
        p_stop_loss: (data.stopLoss ?? null) as number,
        p_take_profit: (data.takeProfit ?? null) as number,
        p_payload: (data.playbookMatch ? { playbookMatch: data.playbookMatch } : null) as never,
      },
    );
    if (rpcError) throw new Error(rpcError.message);
    const row = Array.isArray(rpc) ? rpc[0] : rpc;
    if (!row?.inserted) {
      const resetInHours = Math.max(0, 24 - new Date().getUTCHours());
      throw new Error(
        `Daily signals limit reached for ${cfg.plan} plan (${row?.used ?? cfg.limit}/${cfg.limit}). Resets in ${resetInHours}h. Upgrade for more.`,
      );
    }

    // Write to the immutable, publicly-readable market view ledger. Best-effort:
    // failures are logged but never break the user-facing insert.
    try {
      const degraded = (data.degradedFlags ?? {}) as Record<string, unknown>;
      await supabaseAdmin.from("market_view_ledger").insert({
        pair: data.pair,
        final_view: data.action,
        confluence_score: Math.round(data.score),
        setup_readiness_score: data.readiness ?? null,
        agent_votes: (data.agentVotesJson ?? null) as never,
        data_sources_used: (data.dataSources ?? null) as never,
        degraded_flags: (data.degradedFlags ?? null) as never,
        reference_zone: data.entry ?? null,
        invalidation: data.stopLoss ?? null,
        risk_zone_target: data.takeProfit ?? null,
        published_to_telegram: false,
      } as never);
      void degraded;
    } catch {
      // Ledger insert is best-effort; never block signal persistence.
    }

    // Server-side Telegram auto-send. Runs regardless of browser session state.
    // Idempotent via signal_id. Failures are logged but never break the insert.
    if (data.action !== "WAIT") {
      try {
        // Look up the row we just inserted so we have its id for idempotency.
        const { data: latest } = await supabaseAdmin
          .from("signals")
          .select("id")
          .eq("user_id", context.userId)
          .eq("pair", data.pair)
          .eq("action", data.action)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        if (latest?.id) {
          const { sendTelegramSignalServer } = await import(
            "@/lib/telegram/sendTelegramSignal.server"
          );
          // Fire-and-forget — do not block the insert response.
          void sendTelegramSignalServer({
            signalId: latest.id,
            pair: data.pair,
            action: data.action,
            score: data.score,
            entry: data.entry ?? null,
            stopLoss: data.stopLoss ?? null,
            takeProfit: data.takeProfit ?? null,
            lots: data.lots ?? null,
            agentVotes: data.agentVotes ?? null,
            reasoning: data.reasoning ?? null,
            playbookMatch: data.playbookMatch
              ? {
                  similarityPct: data.playbookMatch.similarityPct,
                  playbookName: data.playbookMatch.playbookName,
                  legendArchitect: data.playbookMatch.legendArchitect,
                }
              : null,
          });
        }
      } catch {
        // never block insert on telegram path
      }
    }

    return { ok: true };
  });

// Client-side maybeAutoSendSignal calls this when the price-finite guard
// drops a would-be send. Persisting to audit_log makes silent drops visible.
export const logTelegramSkip = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z
      .object({
        reason: z.string().min(1).max(200),
        pair: z.string().min(1).max(16),
        action: z.string().min(1).max(16).optional(),
        score: z.number().nullable().optional(),
        entry: z.number().nullable().optional(),
        stopLoss: z.number().nullable().optional(),
        takeProfit: z.number().nullable().optional(),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await supabaseAdmin.from("audit_log").insert({
      action: "TELEGRAM_SKIPPED_INVALID_PRICE",
      actor_id: context.userId,
      target_type: "signal",
      metadata: data as never,
    });
    return { ok: true };
  });

// ---------- Site Settings ----------
// SECURITY: full site_settings read is admin-only. It can contain internal
// keys (cost caps, telegram config, daily AI spend, etc.). Public surfaces
// MUST use `getPublicSiteSettings` (allowlisted keys) instead.
const PUBLIC_SITE_SETTING_KEYS = [
  "disclaimer_version",
  "terms_version",
  "risk_disclosure_version",
  "maintenance_mode",
  "announcement",
  "jurisdictions_config",
  "founding_lifetime_spots",
  "pricing_plans",
  "plan_prices",
  "compliance_versions",
  "public_perf_stats",
] as const;

export const getPublicSiteSettings = createServerFn({ method: "GET" }).handler(async () => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data, error } = await supabaseAdmin
    .from("site_settings")
    .select("key, value")
    .in("key", PUBLIC_SITE_SETTING_KEYS as unknown as string[]);
  if (error) throw new Error(error.message);
  return {
    settings: (data ?? []).map((row) => ({
      key: row.key as string,
      value: JSON.stringify(row.value),
    })),
  };
});

export const getSiteSettings = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin.from("site_settings").select("*");
    if (error) throw new Error(error.message);
    return {
      settings: (data ?? []).map((row) => ({
        key: row.key as string,
        value: JSON.stringify(row.value),
      })),
    };
  });

export const adminUpdateSetting = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z.object({ key: z.string().min(1).max(64), valueJson: z.string().max(20000) }).parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    let parsed: unknown;
    try {
      parsed = JSON.parse(data.valueJson);
    } catch {
      throw new Error("Invalid JSON");
    }
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { writeAuditLog } = await import("@/lib/admin-audit.server");
    const { data: prev } = await supabaseAdmin
      .from("site_settings")
      .select("value")
      .eq("key", data.key)
      .maybeSingle();
    const { error } = await supabaseAdmin
      .from("site_settings")
      .upsert({ key: data.key, value: parsed as never, updated_at: new Date().toISOString() });
    if (error) throw new Error(error.message);
    await writeAuditLog({
      action: "ADMIN_UPDATE_SETTING",
      actorId: context.userId,
      targetType: "site_setting",
      targetId: data.key,
      oldValue: prev?.value ?? null,
      newValue: parsed,
    });
    return { ok: true };
  });

// ---------- API Keys ----------
export const adminListApiKeys = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("api_keys")
      .select("name, value, updated_at")
      .order("name");
    if (error) throw new Error(error.message);
    return { keys: data ?? [] };
  });

export const adminUpsertApiKey = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z
      .object({
        name: z.string().min(1).max(64).regex(/^[A-Z0-9_]+$/i),
        value: z.string().min(1).max(2048),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { writeAuditLog } = await import("@/lib/admin-audit.server");
    const { data: prev } = await supabaseAdmin
      .from("api_keys")
      .select("value")
      .eq("name", data.name)
      .maybeSingle();
    const { error } = await supabaseAdmin
      .from("api_keys")
      .upsert({ name: data.name, value: data.value, updated_at: new Date().toISOString() });
    if (error) throw new Error(error.message);
    await writeAuditLog({
      action: "ADMIN_UPSERT_API_KEY",
      actorId: context.userId,
      targetType: "api_key",
      targetId: data.name,
      // Never log raw secret values — only presence + length.
      oldValue: { present: Boolean(prev?.value), length: (prev?.value as string | undefined)?.length ?? 0 },
      newValue: { present: true, length: data.value.length },
    });
    return { ok: true };
  });

export const adminDeleteApiKey = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => z.object({ name: z.string().min(1).max(64) }).parse(i))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { writeAuditLog } = await import("@/lib/admin-audit.server");
    const { error } = await supabaseAdmin.from("api_keys").delete().eq("name", data.name);
    if (error) throw new Error(error.message);
    await writeAuditLog({
      action: "ADMIN_DELETE_API_KEY",
      actorId: context.userId,
      targetType: "api_key",
      targetId: data.name,
      oldValue: { present: true },
      newValue: { present: false },
    });
    return { ok: true };
  });


// Shared key fetch (any authenticated user can read shared keys)
export const getSharedApiKey = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => z.object({ name: z.string().min(1).max(64) }).parse(i))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row } = await supabaseAdmin
      .from("api_keys")
      .select("value")
      .eq("name", data.name)
      .maybeSingle();
    return { value: (row?.value as string | undefined) ?? null };
  });

// ---------- Attach playbook match to most recent signal for this pair ----------
export const attachPlaybookToLatestSignal = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z
      .object({
        pair: z.string().min(1).max(16),
        playbookMatch: z.object({
          playbookName: z.string().min(1).max(120),
          legendArchitect: z.string().min(1).max(80),
          similarityPct: z.number().min(0).max(100),
          bias: z.enum(["BUY", "SELL", "WAIT"]),
        }),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: latest } = await supabaseAdmin
      .from("signals")
      .select("id, payload")
      .eq("user_id", context.userId)
      .eq("pair", data.pair)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (!latest) return { ok: false };
    const nextPayload = {
      ...((latest.payload as Record<string, unknown> | null) ?? {}),
      playbookMatch: data.playbookMatch,
    };
    const { error } = await supabaseAdmin
      .from("signals")
      .update({ payload: nextPayload })
      .eq("id", latest.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
